import React, { createContext, useContext, useState, useEffect } from 'react'
import { authService } from '../services/authService'
import toast from 'react-hot-toast'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState(localStorage.getItem('token'))

  // Verificar token ao carregar
  useEffect(() => {
    if (token) {
      verifyToken()
    } else {
      setLoading(false)
    }
  }, [])

  const verifyToken = async () => {
    try {
      const userData = await authService.verifyToken()
      setUser(userData.user)
    } catch (error) {
      // Só faz logout se o servidor explicitamente rejeitou o token (401/403)
      // Erros de rede, timeout ou servidor reiniciando não devem deslogar o usuário
      const status = error?.response?.status
      if (status === 401 || status === 403) {
        console.error('Token inválido:', error)
        logout()
      } else {
        console.warn('Falha ao verificar token (erro de rede/timeout) — mantendo sessão:', error?.code || error?.message)
        // Mantém o usuário do localStorage se disponível
        const savedUser = localStorage.getItem('user')
        if (savedUser) {
          try { setUser(JSON.parse(savedUser)) } catch (_) {}
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const login = async (email, password) => {
    try {
      setLoading(true)
      const response = await authService.login(email, password)
      
      setUser(response.user)
      setToken(response.token)
      localStorage.setItem('token', response.token)
      localStorage.setItem('user', JSON.stringify(response.user))
      if (response.refreshToken) localStorage.setItem('refreshToken', response.refreshToken)

      toast.success(`Bem-vindo, ${response.user.name}!`)
      return { success: true, must_change_password: response.must_change_password === true }
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao fazer login'
      toast.error(message)
      return { success: false, error: message }
    } finally {
      setLoading(false)
    }
  }

  const logout = async () => {
    try {
      // Tentativa opcional de chamar API de logout
      if (token) {
        try {
          await authService.logout()
        } catch (apiError) {
          // Ignora erro da API - logout local sempre funciona
          console.log('Logout API não disponível, fazendo logout local')
        }
      }
    } catch (error) {
      console.error('Erro no logout:', error)
    } finally {
      // Sempre limpa os dados locais, independente de erro
      setUser(null)
      setToken(null)
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
      toast.success('Logout realizado com sucesso!')
    }
  }

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }))
  }

  const isAuthenticated = !!user && !!token

  const hasPermission = (requiredRoles = []) => {
    if (!user) return false
    if (requiredRoles.length === 0) return true
    return requiredRoles.includes(user.role)
  }

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    login,
    logout,
    updateUser,
    hasPermission
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContext